import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ChevronLeft, CheckCircle2, XCircle, CalendarX, RefreshCw, CreditCard,
    Wallet, Banknote, Gift, ShieldAlert, ArrowRight, Clock, User,
    FileText, MailCheck, RotateCcw, AlertTriangle,
} from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { formatMoney } from "@/lib/format";
import { Skeleton, ErrorState } from "@/components/BookingShell";
import { invalidate } from "@/lib/queryCache";
import {VisitAppointment} from '@/components/VisitAppointment';

const OUTCOMES = [
    { key: "completed",              label: "Completed",              icon: CheckCircle2, tone: "emerald", desc: "Treatment carried out. Continue to payment + automations." },
    { key: "no_show",                label: "No show",                icon: CalendarX,    tone: "amber",   desc: "Customer didn't arrive. Stops all follow-up automations." },
    { key: "cancelled_by_customer",  label: "Cancelled by customer",  icon: XCircle,      tone: "red",     desc: "Cancelled by the customer. No aftercare, no review." },
    { key: "cancelled_by_clinic",    label: "Cancelled by clinic",    icon: ShieldAlert,  tone: "red",     desc: "Cancelled by Pout. No aftercare, no review." },
    { key: "rescheduled",            label: "Rescheduled",            icon: RotateCcw,    tone: "gold",    desc: "Moved to a new date. Automations resume at the new appointment." },
];

const PAYMENT_METHODS = [
    { key: "card_machine",  label: "Card Machine",            icon: CreditCard, desc: "SumUp / terminal. Portal records only — Angela takes payment separately." },
    { key: "cash",          label: "Cash",                    icon: Wallet,     desc: "Recorded, not processed." },
    { key: "bank_transfer", label: "Bank Transfer",           icon: Banknote,   desc: "Confirmed transfer received." },
    { key: "gift_voucher",  label: "Gift Voucher",            icon: Gift,       desc: "Voucher redeemed." },
    { key: "deposit_only",  label: "Deposit Only / Consult",  icon: FileText,   desc: "No further payment collected today." },
];

const TONES = {
    emerald: "border-emerald-400/50 bg-emerald-500/10 text-emerald-100",
    amber:   "border-amber-400/50 bg-amber-500/10 text-amber-100",
    red:     "border-red-400/50 bg-red-500/10 text-red-100",
    gold:    "border-gold/50 bg-gold/10 text-gold",
};

export function AppointmentCheckout() {
    const { id } = useParams();
    const nav = useNavigate();

    const [appt, setAppt] = useState(null);
    const [error, setError] = useState(null);
    const [step, setStep] = useState(1);         // 1=outcome, 2=payment, 3=confirm
    const [outcome, setOutcome] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [amountCollected, setAmountCollected] = useState("");
    const [treatmentChanged, setTreatmentChanged] = useState(false);
    const [changeReason, setChangeReason] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setError(null);
        staffApi.getAppointment(id).then(setAppt).catch(setError);
    }, [id]);

    // Derived pricing snapshot
    const priceSnapshot = useMemo(() => {
        if (!appt) return null;
        const raw = appt.raw || appt;
        const booking = (raw.bookings || [])[0] || {};
        const service_price = Number(raw.price || booking.price || 0);
        const payments = booking.payments || [];
        const deposit_paid = payments
            .filter((p) => (p.status || "").toLowerCase() === "paid")
            .reduce((s, p) => s + Number(p.amount || 0), 0);
        const outstanding_before = Math.max(service_price - deposit_paid, 0);
        return { service_price, deposit_paid, outstanding_before, service_name: raw.serviceName || booking.serviceName };
    }, [appt]);

    const amt = Number(amountCollected || 0);
    const outstanding_after = priceSnapshot ? Math.max(priceSnapshot.outstanding_before - amt, 0) : 0;
    if (appt?.use_visit || (appt?.visit_appointment_ids || []).length > 1) return <VisitAppointment appointmentId={id} checkoutMode/>;

    if (error) return <ErrorState error={error} onRetry={() => setError(null) || setAppt(null) || staffApi.getAppointment(id).then(setAppt).catch(setError)} />;
    if (!appt || !priceSnapshot) return <Skeleton className="h-96" />;

    const canProceedFromOutcome = !!outcome;
    const canProceedFromPayment = outcome !== "completed" || (paymentMethod && amountCollected !== "");

    const submit = async () => {
        setSaving(true);
        try {
            const payload = {
                outcome,
                payment_method: outcome === "completed" ? paymentMethod : null,
                amount_collected: outcome === "completed" ? Number(amountCollected || 0) : 0,
                treatment_changed: treatmentChanged,
                change_reason: changeReason.trim() || null,
                practitioner_notes: notes.trim() || null,
            };
            const res = await staffApi.saveCheckout(id, payload);
            invalidate("staff:appointments");
            invalidate("staff:dashboard");
            invalidate("staff:calendar");
            invalidate("staff:payments");
            invalidate(`staff:appointment:${id}`);
            toast.success("Checkout complete.");
            nav(`/staff/appointments/${id}`, { replace: true });
            return res;
        } catch (e) {
            toast.error(e.message || "Could not complete checkout.");
        } finally { setSaving(false); }
    };

    const cust = ((appt.raw || appt).bookings || [{}])[0].customer || appt.customer || {};

    return (
        <div className="space-y-5 md:space-y-6" data-testid="checkout-page">
            <Link to={`/staff/appointments/${id}`} className="text-xs text-white/50 hover:text-gold inline-flex items-center gap-1">
                <ChevronLeft className="h-3.5 w-3.5" /> Back to appointment
            </Link>

            <header>
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold/85">Checkout</div>
                <h1 className="font-serif text-3xl md:text-4xl text-white">{priceSnapshot.service_name || "Appointment"}</h1>
                <p className="text-sm text-white/50 mt-1">
                    {cust.firstName} {cust.lastName}
                    {cust.email ? ` · ${cust.email}` : ""}
                </p>
            </header>

            {/* Step indicator */}
            <ol className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em]" data-testid="checkout-steps">
                {["Outcome", "Payment", "Confirm"].map((s, i) => (
                    <li key={s} className={`px-3 py-1.5 rounded-full border ${step === i + 1 ? "border-gold text-gold" : step > i + 1 ? "border-emerald-400/40 text-emerald-200" : "border-white/10 text-white/40"}`}>
                        {i + 1}. {s}
                    </li>
                ))}
            </ol>

            {step === 1 && (
                <section className="space-y-3" data-testid="checkout-step-outcome">
                    <h2 className="font-serif text-xl text-white">1 · Appointment outcome</h2>
                    <p className="text-xs text-white/50">Only <strong>Completed</strong> appointments continue to payment and trigger automations.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                        {OUTCOMES.map((o) => (
                            <button key={o.key} type="button" onClick={() => setOutcome(o.key)}
                                    data-testid={`outcome-${o.key}`}
                                    className={`text-left rounded-2xl border p-4 transition-all ${outcome === o.key ? TONES[o.tone] : "border-white/10 hover:border-white/30 bg-white/[0.02] text-white"}`}>
                                <div className="flex items-center gap-2">
                                    <o.icon className="h-4 w-4" strokeWidth={1.5} />
                                    <div className="font-serif text-lg">{o.label}</div>
                                </div>
                                <div className="text-xs opacity-80 mt-1">{o.desc}</div>
                            </button>
                        ))}
                    </div>
                    <div className="flex justify-end pt-2">
                        <button type="button" disabled={!canProceedFromOutcome}
                                onClick={() => setStep(outcome === "completed" ? 2 : 3)}
                                data-testid="outcome-continue"
                                className="rounded-full bg-gold text-navy px-5 h-10 font-semibold text-sm disabled:opacity-30 flex items-center gap-1">
                            Continue <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </section>
            )}

            {step === 2 && outcome === "completed" && (
                <section className="space-y-4" data-testid="checkout-step-payment">
                    <h2 className="font-serif text-xl text-white">2 · Payment</h2>

                    <div className="rounded-2xl glass p-4 md:p-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="checkout-price-snapshot">
                        <Field label="Booked treatment" value={priceSnapshot.service_name || "—"} />
                        <Field label="Treatment cost" value={formatMoney(priceSnapshot.service_price)} />
                        <Field label="Deposit paid" value={formatMoney(priceSnapshot.deposit_paid)} />
                        <Field label="Remaining balance" value={formatMoney(priceSnapshot.outstanding_before)} tone="amber" />
                    </div>

                    {/* Treatment changed override */}
                    <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3 cursor-pointer" data-testid="treatment-changed-toggle">
                        <input type="checkbox" checked={treatmentChanged} onChange={(e) => setTreatmentChanged(e.target.checked)} className="mt-1" />
                        <div>
                            <div className="text-sm text-white">Treatment changed during the appointment</div>
                            <div className="text-xs text-white/50">Records what actually happened without modifying the original booking. Use for exceptional cases only.</div>
                            {treatmentChanged && (
                                <input type="text" value={changeReason} onChange={(e) => setChangeReason(e.target.value)}
                                       placeholder="Reason (optional)"
                                       data-testid="treatment-change-reason"
                                       className="mt-2 w-full bg-transparent border-b border-white/20 text-sm text-white placeholder-white/30 outline-none focus:border-gold" />
                            )}
                        </div>
                    </label>

                    <div>
                        <h3 className="text-[10px] uppercase tracking-[0.24em] text-white/55 mb-2">Payment method</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                            {PAYMENT_METHODS.map((m) => (
                                <button key={m.key} type="button" onClick={() => setPaymentMethod(m.key)}
                                        data-testid={`payment-method-${m.key}`}
                                        className={`text-left rounded-2xl border p-4 transition-all ${paymentMethod === m.key ? "border-gold text-white bg-gold/10" : "border-white/10 hover:border-white/30 bg-white/[0.02] text-white"}`}>
                                    <div className="flex items-center gap-2">
                                        <m.icon className="h-4 w-4" strokeWidth={1.5} />
                                        <div className="font-serif text-lg">{m.label}</div>
                                    </div>
                                    <div className="text-xs text-white/50 mt-1">{m.desc}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {paymentMethod && (
                        <div className="rounded-2xl border border-gold/40 bg-gold/5 p-4 space-y-2" data-testid="amount-collected-panel">
                            <div className="text-xs text-white/70">Amount received</div>
                            <div className="flex items-center gap-3">
                                <span className="font-serif text-2xl text-gold">£</span>
                                <input type="number" step="0.01" min="0"
                                       value={amountCollected}
                                       onChange={(e) => setAmountCollected(e.target.value)}
                                       placeholder="0.00"
                                       data-testid="amount-collected-input"
                                       className="bg-transparent border-b border-white/25 text-2xl font-serif text-white outline-none focus:border-gold flex-1" />
                                <button type="button" onClick={() => setAmountCollected(priceSnapshot.outstanding_before.toFixed(2))}
                                        className="text-[10px] uppercase tracking-[0.18em] text-white/60 hover:text-gold" data-testid="amount-fill-balance">
                                    Fill balance
                                </button>
                            </div>
                            <div className="text-xs text-white/50">
                                Outstanding after this payment: <span className={outstanding_after > 0 ? "text-amber-200" : "text-emerald-200"}>{formatMoney(outstanding_after)}</span>
                            </div>
                        </div>
                    )}

                    <textarea placeholder="Practitioner notes (optional, kept internal)"
                              value={notes} onChange={(e) => setNotes(e.target.value)}
                              data-testid="practitioner-notes"
                              rows={3}
                              className="w-full rounded-xl border border-white/10 bg-white/[0.02] p-3 text-sm text-white placeholder-white/30 outline-none focus:border-gold" />

                    <div className="flex justify-between pt-2">
                        <button type="button" onClick={() => setStep(1)} className="text-white/60 text-sm">Back</button>
                        <button type="button" disabled={!canProceedFromPayment}
                                onClick={() => setStep(3)}
                                data-testid="payment-continue"
                                className="rounded-full bg-gold text-navy px-5 h-10 font-semibold text-sm disabled:opacity-30 flex items-center gap-1">
                            Continue <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </section>
            )}

            {step === 3 && (
                <section className="space-y-4" data-testid="checkout-step-confirm">
                    <h2 className="font-serif text-xl text-white">3 · Confirm & complete</h2>
                    <p className="text-xs text-white/50">Please review before pressing Complete Checkout. Automations will be triggered immediately.</p>

                    <div className="rounded-2xl glass p-4 md:p-5 space-y-3" data-testid="checkout-summary">
                        <SummaryRow label="Appointment" value={priceSnapshot.service_name || "—"} />
                        <SummaryRow label="Outcome" value={<Outcome badge outcome={outcome} />} />
                        {outcome === "completed" && (
                            <>
                                <SummaryRow label="Deposit paid" value={formatMoney(priceSnapshot.deposit_paid)} />
                                <SummaryRow label="Remaining balance" value={formatMoney(priceSnapshot.outstanding_before)} />
                                <SummaryRow label="Payment method" value={PAYMENT_METHODS.find(m => m.key === paymentMethod)?.label || "—"} />
                                <SummaryRow label="Amount received" value={formatMoney(amt)} />
                                <SummaryRow label="Outstanding balance" value={formatMoney(outstanding_after)} tone={outstanding_after > 0 ? "amber" : "emerald"} />
                                {treatmentChanged && <SummaryRow label="Treatment changed" value={changeReason || "Recorded"} />}
                            </>
                        )}
                    </div>

                    {outcome === "completed" && (
                        <div className="rounded-2xl border border-gold/40 bg-gold/5 p-4" data-testid="triggered-automations">
                            <div className="text-[10px] uppercase tracking-[0.24em] text-gold/85 mb-2">The following automations will now be triggered</div>
                            <ul className="text-sm text-white/85 space-y-1.5">
                                <li className="flex items-center gap-2"><MailCheck className="h-3.5 w-3.5 text-gold" /> Treatment aftercare email</li>
                                <li className="flex items-center gap-2"><MailCheck className="h-3.5 w-3.5 text-gold" /> Review request (per treatment timing)</li>
                                <li className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-gold" /> Future rebooking reminder (category default)</li>
                                <li className="flex items-center gap-2"><User className="h-3.5 w-3.5 text-gold" /> Customer timeline updated</li>
                            </ul>
                        </div>
                    )}
                    {outcome !== "completed" && (
                        <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-4 text-amber-100 text-sm flex items-start gap-2" data-testid="stopped-automations">
                            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                            <div>Aftercare, reviews and rebooking reminders will <strong>not</strong> be scheduled for this appointment.</div>
                        </div>
                    )}

                    <div className="flex justify-between pt-2">
                        <button type="button" onClick={() => setStep(outcome === "completed" ? 2 : 1)} className="text-white/60 text-sm">Back</button>
                        <button type="button" disabled={saving} onClick={submit}
                                data-testid="complete-checkout"
                                className="rounded-full bg-gold text-navy px-6 h-10 font-semibold text-sm disabled:opacity-50 flex items-center gap-2">
                            {saving ? "Completing…" : "Complete checkout"}
                            {!saving && <CheckCircle2 className="h-4 w-4" />}
                        </button>
                    </div>
                </section>
            )}
        </div>
    );
}

function Field({ label, value, tone }) {
    const toneClass = tone === "amber" ? "text-amber-200" : "text-white";
    return (
        <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-[0.18em] text-white/45">{label}</div>
            <div className={`font-serif text-lg mt-0.5 ${toneClass} truncate`}>{value}</div>
        </div>
    );
}

function SummaryRow({ label, value, tone }) {
    const toneClass = tone === "amber" ? "text-amber-200" : tone === "emerald" ? "text-emerald-200" : "text-white";
    return (
        <div className="flex justify-between items-center gap-3">
            <div className="text-xs text-white/55 uppercase tracking-[0.14em]">{label}</div>
            <div className={`text-sm font-medium ${toneClass}`}>{value}</div>
        </div>
    );
}

function Outcome({ outcome }) {
    const o = OUTCOMES.find(x => x.key === outcome);
    if (!o) return <span>—</span>;
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs ${TONES[o.tone]}`}><o.icon className="h-3 w-3" />{o.label}</span>;
}
